import axios from "axios";

// V2.2E-R · Normalize the API base so the SPA is safe against blank /
// absent / trailing-slash values of REACT_APP_BACKEND_URL.
//
// Prior behavior: `${undefined}/api` produced the literal string
// "undefined/api" for every axios call when the env var was absent —
// silently bypassing the same-origin E2E proxy and blowing up in the
// browser. The three legitimate configurations are:
//   1. `undefined` / `""` / `"   "`  → same-origin: use `/api`
//   2. absolute URL without trailing slash (e.g. `https://x.example`) → `<URL>/api`
//   3. absolute URL with trailing slash (e.g. `https://x.example/`)   → `<URL>/api`
// Anything else (relative path like `"/backend"`) still gets `/api`
// appended verbatim — callers who want that must supply a well-formed
// absolute URL.
export function _resolveApiBase(rawUrl) {
  if (typeof rawUrl !== "string") return "/api";
  const trimmed = rawUrl.trim();
  if (trimmed === "") return "/api";
  const withoutTrailing = trimmed.replace(/\/+$/, "");
  return `${withoutTrailing}/api`;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = _resolveApiBase(BACKEND_URL);

// P1 refresh-token rotation (Feb 2026).
// -------------------------------------
// Access token: short-lived (~15 min) HS256 JWT returned in login/refresh
// response bodies. Held primarily in this module-level variable and
// MIRRORED to `sessionStorage` for F5 durability within the same tab.
//
// Why sessionStorage (Session 58 · Feb 2026):
//   Independent-verification tester reported the first-login hang
//   persisted through a hard F5 refresh. Root cause: the module-level
//   `_accessToken` is destroyed on every hard reload, so Schedule's
//   business-endpoint queries fire without a Bearer, trigger the
//   silent /auth/refresh cascade, and — because that POST had no
//   explicit timeout — could stall indefinitely, hanging the request
//   interceptor's `await _doRefresh()` and starving every subsequent
//   GET. sessionStorage is:
//     * scoped to the current tab (cleared on tab close),
//     * NOT accessible to other origins,
//     * gone if the user closes the tab (so it's not a durable XSS
//       exfil surface like localStorage — an attacker who lands JS in
//       our origin already has a live in-memory token anyway).
//   The refresh cookie remains the source of truth across tabs and
//   browser restarts; sessionStorage only closes the F5 gap.
const _SESSION_STORAGE_KEY = "pec_access_token_v1";
let _accessToken = null;
try {
  if (typeof window !== "undefined" && window.sessionStorage) {
    _accessToken = window.sessionStorage.getItem(_SESSION_STORAGE_KEY) || null;
  }
} catch { /* private-browsing / disabled storage */ }

export function setAccessToken(token) {
  _accessToken = token || null;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (_accessToken) {
        window.sessionStorage.setItem(_SESSION_STORAGE_KEY, _accessToken);
      } else {
        window.sessionStorage.removeItem(_SESSION_STORAGE_KEY);
      }
    }
  } catch { /* no-op */ }
}

export function getAccessToken() {
  return _accessToken;
}

export function clearAccessToken() {
  _accessToken = null;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.removeItem(_SESSION_STORAGE_KEY);
    }
  } catch { /* no-op */ }
}

// Session 58 (Feb 2026) — poisoned axios-dedupe defense.
// The `_inFlight` map below can retain a promise whose response never
// arrives if the request interceptor's silent-refresh await stalls.
// Any subsequent GET with the same key would then deduplicate onto
// the stuck promise and hang forever. We wipe this map on every auth
// state transition (login / logout) so a fresh session cannot inherit
// a stuck entry from the pre-login cold-start probes.
export function clearInFlightCache() {
  try { _inFlight.clear(); } catch { /* no-op */ }
}

const api = axios.create({ baseURL: API, withCredentials: true });

// Attach the access token as a Bearer header on every request. The
// backend also accepts the legacy `access_token` cookie as a fallback,
// but new flows write ONLY to this in-memory ref.
//
// Package 2 · Item 2 (Feb 2026) — cold-start gate.
// On hard-reload the module-level `_accessToken` is empty. Any
// protected endpoint fired before the silent refresh completes (or
// concurrently with `/auth/me`'s cookie-fallback path) would 401 →
// refresh → retry. That dance is functionally harmless but produces
// scary red network-tab entries on every page reload. Solution:
// BEFORE dispatch, if we don't yet have a token AND the endpoint is
// not itself part of the auth flow, `await _doRefresh()`
// (single-flight) so the Bearer header can be attached correctly.
// Refresh failure is swallowed — the request still fires and the
// existing response interceptor handles the 401→/login bounce.
api.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const isAuthFlowEndpoint =
    AUTH_NO_REFRESH.some((p) => url.includes(p)) || url.includes("/auth/me");
  if (!_accessToken && !isAuthFlowEndpoint) {
    try { await _doRefresh(); } catch { /* let the request fire & 401 */ }
  }
  if (_accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// -----------------------------------------------------------------------------
// Feb 2026 OPT-1 · Item 1 — In-flight GET dedupe.
//
// The schedule board's cold-load and date-change flows produce a burst of ~10
// GETs. Production logs showed each URL fired twice (a common pattern when
// effects re-run in quick succession under React 18 concurrent rendering).
// This small in-flight cache collapses concurrent identical GETs onto ONE
// underlying network request. Mutations, POSTs, PUTs, PATCHes, DELETEs are
// never deduped — only idempotent GET.
//
// Semantics:
//   - key = `${method}:${baseURL}${url}?${sortedParams}`
//   - if a request with the same key is already in flight, the new caller
//     awaits and receives the same axios response object.
//   - once resolved/rejected, the entry is removed so the NEXT call (e.g. a
//     refetch after mutation) actually hits the network.
//   - the dedupe window is intentionally exact-duplicate + in-flight only. A
//     stale-time / freshness cache is layered ABOVE this via TanStack Query
//     (see index.js QueryClient config).
// -----------------------------------------------------------------------------
const _inFlight = new Map();

function _dedupeKey(config) {
  const method = (config.method || "get").toLowerCase();
  const base = config.baseURL || "";
  const url = config.url || "";
  const paramsObj = config.params || {};
  const paramStr = Object.keys(paramsObj)
    .sort()
    .map((k) => `${k}=${paramsObj[k]}`)
    .join("&");
  return `${method}:${base}${url}?${paramStr}`;
}

api.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() !== "get") return config;
  // Callers can opt out by setting `_dedupe: false` on the axios config (rare;
  // e.g. probes that intentionally need a fresh network hit).
  if (config._dedupe === false) return config;
  const key = _dedupeKey(config);
  const pending = _inFlight.get(key);
  if (pending) {
    // Attach an adapter that returns the pending promise's resolved response.
    // Preserves the axios contract (`.data`, `.status`, `.headers`).
    config.adapter = () => pending.then(
      (resp) => ({ ...resp, config, request: null }),
      (err) => Promise.reject(err),
    );
    return config;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => {
    if ((resp.config.method || "get").toLowerCase() === "get"
        && resp.config._dedupe !== false) {
      const key = _dedupeKey(resp.config);
      _inFlight.delete(key);
    }
    return resp;
  },
  (error) => {
    const cfg = error?.config;
    if (cfg && (cfg.method || "get").toLowerCase() === "get"
        && cfg._dedupe !== false) {
      _inFlight.delete(_dedupeKey(cfg));
    }
    return Promise.reject(error);
  },
);

// Register the actual network promise into `_inFlight` AFTER axios has run
// through the request interceptors. Uses a wrapping request interceptor so it
// runs last. We can't set the promise in the same interceptor above because
// `pending` there is the previously-registered call, not the current one.
api.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() !== "get") return config;
  if (config._dedupe === false) return config;
  if (config.adapter) return config;  // already deduped in the check above
  const key = _dedupeKey(config);
  // Wrap the default adapter so we register the promise on first dispatch.
  const originalAdapter = axios.defaults.adapter;
  config.adapter = (cfg) => {
    const promise = originalAdapter(cfg);
    _inFlight.set(key, promise);
    return promise;
  };
  return config;
});

// Endpoints that must NOT trigger the silent refresh flow (any 401 from
// these is definitive and the caller handles it):
// - /auth/me is probed on boot before the user has logged in
// - /auth/login returns 401 on bad credentials; the page handles the toast
// - /auth/refresh returning 401 IS the "session dead" signal
// - /auth/logout doesn't need refresh
const AUTH_NO_REFRESH = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
];

// Endpoints where a definitive 401 should NOT bounce to /login (the
// caller handles the failure). The refresh interceptor still runs
// for these — they're just excluded from the fallback redirect.
const AUTH_NO_REDIRECT = [
  "/auth/me",
  "/auth/login",
];

// Routes the SPA already considers "unauthenticated" — never bounce to /login from these.
const UNAUTH_PAGES = ["/login"];

// P1 refresh-token rotation (Feb 2026) — silent refresh flow.
// -----------------------------------------------------------
// On 401 from any protected endpoint:
//   1. If the failed request was itself an auth-flow endpoint, or was
//      already a retry, we don't attempt refresh — return the 401.
//   2. Otherwise, POST /api/auth/refresh (using the httpOnly refresh
//      cookie the browser auto-attaches).
//        - On success: update _accessToken, retry the original request
//          once, and return that response.
//        - On failure: clear _accessToken, bounce to /login.
// A single-flight `_refreshPromise` dedupes concurrent 401s so a burst
// of parallel expired-token requests results in ONE refresh call.

let _refreshPromise = null;

// Session 58 (Feb 2026) — explicit refresh-timeout ceiling.
// Without this the axios POST /auth/refresh call defaults to no
// timeout. If the backend or ingress stalls, `_refreshPromise` never
// settles → every subsequent request that hits the request-interceptor
// `await _doRefresh()` (or a caller of `whenAccessTokenReady`) hangs
// on that stuck promise → the app appears to freeze mid-load. 4000ms
// is well above the p99 refresh latency (~40ms in production) and
// well under the AuthContext checkAuth ceiling (6000ms), so failures
// still surface as a "session dead" bounce to /login rather than an
// indefinite spinner.
const REFRESH_TIMEOUT_MS = 4000;

function _doRefresh() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = axios
    .post(`${API}/auth/refresh`, null, {
      withCredentials: true,
      timeout: REFRESH_TIMEOUT_MS,
    })
    .then((res) => {
      const token = res?.data?.access_token;
      if (!token) throw new Error("refresh returned no access_token");
      setAccessToken(token);
      return token;
    })
    .catch((e) => {
      clearAccessToken();
      throw e;
    })
    .finally(() => {
      _refreshPromise = null;
    });
  return _refreshPromise;
}

// Package 2 · Item 2 (Feb 2026) — cold-load 401 noise fix.
// ---------------------------------------------------------
// On a hard-reload the module-level `_accessToken` is empty; the
// cookie-based `/auth/me` fallback lets us confirm the session but
// leaves us without a Bearer token for subsequent protected calls
// like `/whoami/permissions`. Callers that would otherwise 401 →
// refresh → retry (network-tab noise, harmless) can now `await
// whenAccessTokenReady()` first. It resolves immediately when a
// token is already in memory, otherwise kicks off (or reuses) an
// in-flight refresh and resolves when that completes. Rejection is
// swallowed here — callers still fire their own request and the
// existing 401 interceptor handles the recovery, so this is purely
// an optimization to avoid the cosmetic 401.
export function whenAccessTokenReady() {
  if (_accessToken) return Promise.resolve(_accessToken);
  return _doRefresh().catch(() => null);
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config;
    const url = cfg?.url || "";
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAuthFlowEndpoint = AUTH_NO_REFRESH.some((p) => url.includes(p));
    const isAuthNoRedirect = AUTH_NO_REDIRECT.some((p) => url.includes(p));
    const onUnauthPage = UNAUTH_PAGES.includes(path);

    if (status === 401 && cfg && !cfg._retriedAfterRefresh && !isAuthFlowEndpoint) {
      // First attempt at silent refresh + retry.
      try {
        await _doRefresh();
        cfg._retriedAfterRefresh = true;
        return api.request(cfg);
      } catch (_refreshErr) {
        // Refresh failed — session is truly dead. Fall through to the
        // redirect logic below.
      }
    }

    if (status === 401 && !isAuthNoRedirect && !onUnauthPage) {
      // Session unrecoverable — send the user back to login. Full
      // window.location bounce also resets any cached SPA state.
      clearAccessToken();
      window.location.assign("/login");
    }
    return Promise.reject(error);
  },
);

// -----------------------------------------------------------------------------
// Feb 2026 OPT-1 · Item 1 — Auto-invalidate the TanStack Query cache after any
// successful mutation. The mapping is by URL prefix; anything hitting
// `/cars/*` with a non-GET method invalidates every `['ref', 'cars']` query,
// which triggers an automatic refetch in any component that subscribes.
//
// This is the reason Fleet / Coaches / Coordinators pages don't need per-page
// `queryClient.invalidateQueries` calls — they just POST/PUT/DELETE as before
// and the interceptor keeps the shared cache honest.
//
// Prefix-based so nested paths still match (e.g. `/cars/bulk-status`,
// `/coaches/${id}/lead-rank`, `/sessions/${id}/notes`).
// -----------------------------------------------------------------------------
import { queryClient } from "@/lib/queryClient";

const INVALIDATIONS = [
  // URL prefix (after `/api`), list of TanStack Query key-prefixes to invalidate.
  { prefix: "/cars",                   keys: [["ref", "cars"]] },
  { prefix: "/coaches",                keys: [["ref", "coaches"]] },
  { prefix: "/coordinators",           keys: [["ref", "coordinators"]] },
  { prefix: "/bookings",               keys: [["ref", "bookings"]] },
  { prefix: "/sessions",               keys: [["sched", "sessions"], ["sched", "conflicts"]] },
  { prefix: "/session-notes",          keys: [["sched", "session-notes"]] },
  { prefix: "/session-overrides",      keys: [["sched", "session-overrides"], ["sched", "sessions"]] },
  { prefix: "/session-car-overrides",  keys: [["sched", "session-car-overrides"], ["sched", "sessions"]] },
  { prefix: "/session-coach-overrides",keys: [["sched", "session-coach-overrides"], ["sched", "sessions"]] },
  { prefix: "/capacity-markers",       keys: [["sched", "capacity-markers"]] },
  { prefix: "/availability",           keys: [["sched", "availability"]] },
  { prefix: "/room-bookings",          keys: [["sched", "rooms"]] },
  { prefix: "/schedule",               keys: [["sched"]] },  // any /schedule/* mutation → invalidate everything under ['sched']
  { prefix: "/ice-water",              keys: [["sched", "ice-water"]] },
  { prefix: "/track-sweeps",           keys: [["sched", "track-sweeps"]] },
  // Feb 2026 OPT-2 · Tile-extras batching. Coach-assignment and car-
  // assignment override collections are consumed via GET
  // /schedule/tile-extras; mutating them must refresh the batch AND the
  // legacy per-session GETs (in case a non-batch consumer is mounted).
  { prefix: "/coach-overrides",        keys: [["sched", "tile-extras"]] },
  { prefix: "/car-overrides",          keys: [["sched", "tile-extras"]] },
  { prefix: "/ops-manual-entries",     keys: [["sched", "tile-extras"]] },
  // SpeedAssign Core (Feb 2026 · Phase 3) — Day workspace unified read
  // model. Any /api/v2/manual/* mutation OR /api/import-day/apply must
  // invalidate the day summary so the coverage/issues chips + Coach/
  // Fleet grid views refresh in lockstep. Uses a broad ["day-summary"]
  // family invalidation (all dates) because a single mutation MAY
  // touch multiple days (e.g., import-day/apply on a multi-date file).
  { prefix: "/v2/manual",              keys: [["day-summary"], ["sched", "sessions"], ["sched", "conflicts"]] },
  { prefix: "/import-day/apply",       keys: [["sched", "conflicts"]] },
  { prefix: "/import-day",             keys: [["day-summary"], ["sched", "sessions"], ["ref", "bookings"]] },
];

api.interceptors.response.use((resp) => {
  const method = (resp.config.method || "get").toLowerCase();
  if (method === "get") return resp;
  const url = resp.config.url || "";
  for (const { prefix, keys } of INVALIDATIONS) {
    if (url.startsWith(prefix)) {
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));
    }
  }
  return resp;
});

export default api;
